/**
 * Simon Button Component
 * Individual game button with physics-driven positioning
 */

import { ButtonColor } from '../core/SequenceManager';

export interface ButtonPosition {
  x: number;
  y: number;
}

interface ButtonProps {
  color: ButtonColor;
  position: ButtonPosition;
  isActive: boolean;
  isShowingSequence: boolean;
  isInvisible?: boolean;
  isDecoy?: boolean;
  intruderId?: string;
  wearLevel?: number; // 0.0 to 1.0, visual degradation indicator
  onPress: (color: ButtonColor, isDecoy?: boolean, intruderId?: string) => void;
  onProximityReveal?: (color: ButtonColor, reveal: boolean) => void;
}

const colorMap: Record<ButtonColor, string> = {
  red: 'var(--accent-r)',
  green: 'var(--accent-g)',
  blue: 'var(--accent-b)',
  yellow: 'var(--accent-y)'
};

export function Button({ 
  color, 
  position, 
  isActive, 
  isShowingSequence, 
  isInvisible = false,
  isDecoy = false,
  intruderId,
  wearLevel = 0,
  onPress, 
  onProximityReveal 
}: ButtonProps) {
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    if (!isShowingSequence) {
      onPress(color, isDecoy, intruderId);
    }
  };

  const handlePointerEnter = () => {
    if (isInvisible && onProximityReveal) {
      onProximityReveal(color, true);
    }
  };

  const handlePointerLeave = () => {
    if (isInvisible && onProximityReveal) {
      onProximityReveal(color, false);
    }
  };

  // Calculate opacity: invisible mode makes buttons transparent unless revealed
  let baseOpacity = isInvisible ? 0 : (isActive ? 1 : 0.7);
  
  // Apply wear degradation to opacity
  if (wearLevel > 0.4) {
    baseOpacity *= 1 - (wearLevel * 0.3); // Max 30% opacity reduction when fully worn
  }

  // Decoy intruders have a subtle visual difference: pulsing animation and slightly different shadow
  const decoyPulse = isDecoy ? 'intruder-pulse 1.2s ease-in-out infinite' : 'none';
  const decoyShadow = isDecoy 
    ? (isActive ? `0 0 35px ${colorMap[color]}90, 0 0 10px rgba(255,255,255,0.5)` : `0 0 20px ${colorMap[color]}50, 0 0 5px rgba(255,255,255,0.3)`)
    : (isActive ? `0 0 30px ${colorMap[color]}80` : `0 0 15px ${colorMap[color]}40`);
  
  // Wear creates visual cracks/degradation
  const showWearIndicator = wearLevel > 0.4;
  const crackOpacity = Math.min(0.8, (wearLevel - 0.4) * 1.5);

  return (
    <>
      <style>{`
        @keyframes intruder-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes wear-flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
      {/* Wear indicator overlay - positioned absolutely, non-interactive */}
      {showWearIndicator && (
        <div style={{
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: 'clamp(72px, 18vw, 80px)',
          height: 'clamp(72px, 18vw, 80px)',
          minWidth: '60px',
          minHeight: '60px',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: `radial-gradient(circle, transparent 30%, rgba(0,0,0,${crackOpacity * 0.3}) 100%)`,
            border: `2px solid rgba(255,255,255,${crackOpacity * 0.4})`,
            borderStyle: 'dashed',
            opacity: crackOpacity,
            animation: wearLevel > 0.8 ? 'wear-flicker 2s ease-in-out infinite' : 'none',
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-8px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '10px',
            color: wearLevel > 0.8 ? 'rgba(255, 59, 92, 0.9)' : 'rgba(255, 255, 255, 0.6)',
            fontWeight: 700,
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none'
          }}>
            {wearLevel > 0.9 ? '⚠️' : wearLevel > 0.7 ? '⚡' : ''}
          </div>
        </div>
      )}
      {/* Main button */}
      <button
        onPointerDown={handlePointerDown}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        disabled={isShowingSequence}
        style={{
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: 'clamp(72px, 18vw, 80px)',
          height: 'clamp(72px, 18vw, 80px)',
          minWidth: '60px',
          minHeight: '60px',
          borderRadius: '50%',
          border: 'none',
          background: colorMap[color],
          boxShadow: decoyShadow,
          cursor: isShowingSequence ? 'default' : 'pointer',
          transform: isActive ? 'scale(1.2)' : 'scale(1)',
          opacity: baseOpacity,
          animation: decoyPulse,
          transition: 'transform 200ms ease, opacity 400ms ease, box-shadow 200ms ease',
          touchAction: 'none',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          pointerEvents: isShowingSequence ? 'none' : 'auto'
        }}
        aria-label={`${color} button${isDecoy ? ' (intruder)' : ''}${showWearIndicator ? ' (worn)' : ''}`}
      />
    </>
  );
}
